/**
 * Operation Promise Tracker
 * Extracted from WebSocketSingleton.ts
 * Manages pending operation promises with timeout handling
 */

export interface PendingOperationEntry {
  promise: Promise<unknown>;
  resolve: (value: unknown) => void;
  reject: (reason?: unknown) => void;
  timer: NodeJS.Timeout;
}

interface CompletedOperationEntry {
  result: unknown;
  timestamp: number;
}

export class OperationPromiseTracker {
  private pendingOperations: Map<string, PendingOperationEntry> = new Map();
  private completedOperations: Map<string, CompletedOperationEntry> = new Map();
  private defaultTimeoutMs: number;
  private completedResultTtlMs: number;

  constructor(defaultTimeoutMs: number = 4000, completedResultTtlMs: number = 10000) {
    this.defaultTimeoutMs = defaultTimeoutMs;
    this.completedResultTtlMs = completedResultTtlMs;
  }

  /**
   * Await a specific operation's server acknowledgement/result
   * Returns a promise that resolves when the operation result is received
   */
  waitForOperation(
    operationId: string,
    timeoutMs?: number
  ): Promise<unknown> {
    if (!operationId) return Promise.resolve(undefined);

    const completed = this.completedOperations.get(operationId);
    if (completed) {
      if (Date.now() - completed.timestamp <= this.completedResultTtlMs) {
        return Promise.resolve(completed.result);
      }
      this.completedOperations.delete(operationId);
    }

    const existing = this.pendingOperations.get(operationId);
    if (existing) return existing.promise;
    let entry!: Omit<PendingOperationEntry, 'promise'>;
    const promise = new Promise((resolve, reject) => {
      const timeout = timeoutMs || this.defaultTimeoutMs;
      const timer = setTimeout(() => {
        // Timeout: clean resolver and resolve to avoid hanging UX
        this.pendingOperations.delete(operationId);
        resolve(undefined);
      }, timeout);

      entry = { resolve, reject, timer };
    });
    this.pendingOperations.set(operationId, { ...entry, promise });
    return promise;
  }

  /**
   * Notify listeners that an operation received a result from server
   * Resolves the pending promise for this operation
   */
  notifyOperationResult(operationId: string, result: unknown): void {
    if (!operationId) return;

    this.completedOperations.set(operationId, {
      result,
      timestamp: Date.now()
    });

    if (this.completedOperations.size > 200) {
      const cutoff = Date.now() - this.completedResultTtlMs;
      for (const [id, entry] of this.completedOperations.entries()) {
        if (entry.timestamp < cutoff) {
          this.completedOperations.delete(id);
        }
      }
    }

    const entry = this.pendingOperations.get(operationId);
    if (entry) {
      clearTimeout(entry.timer);
      this.pendingOperations.delete(operationId);
      try {
        entry.resolve(result);
      } catch {
        // no-op - promise already resolved/rejected
      }
    }
  }

  /**
   * Notify operation confirmation (alias for result)
   */
  notifyOperationConfirmed(operationId: string, result: unknown): void {
    this.notifyOperationResult(operationId, result);
  }

  /**
   * Check if an operation is being tracked
   */
  isWaitingForOperation(operationId: string): boolean {
    return this.pendingOperations.has(operationId);
  }

  /**
   * Get count of pending operations
   */
  getPendingCount(): number {
    return this.pendingOperations.size;
  }

  /**
   * Clear all pending operations (cancel all waiting)
   */
  clearAll(): void {
    for (const entry of this.pendingOperations.values()) {
      clearTimeout(entry.timer);
      try {
        entry.resolve(undefined);
      } catch {
        // no-op
      }
    }
    this.pendingOperations.clear();
    this.completedOperations.clear();
  }

  /**
   * Cancel a specific pending operation
   */
  cancel(operationId: string): void {
    const entry = this.pendingOperations.get(operationId);
    if (entry) {
      clearTimeout(entry.timer);
      this.pendingOperations.delete(operationId);
      try {
        entry.resolve(undefined);
      } catch {
        // no-op
      }
    }
    this.completedOperations.delete(operationId);
  }
}
